/**
 * marketingAccess — single source of truth for "who may see/use the
 * Marketing Hub".
 *
 * The Marketing module is half-built and intentionally hidden from regular
 * users while UI designers continue iterating on it. Access is gated by:
 *
 *   1. The build-time kill switch `REACT_APP_ENABLE_MARKETING` (defaults
 *      `false`). When this is "false" the module is entirely off — even
 *      super-admins won't see it. Use this if you need an emergency
 *      "hide marketing everywhere" override without touching role logic.
 *
 *   2. The runtime role check `isSuperAdmin(user.role)`. Only the canonical
 *      `super_admin` role (and its aliases — see `ROLE_ALIASES`) may access
 *      the module while it's a prototype.
 *
 * DESIGNER WORKFLOW (important — don't add ad-hoc gates):
 *   • To add a new Marketing page, drop it under `/pages/marketing/*` and
 *     mount its route via the existing `<RequireSuperAdmin>` block in
 *     `App.js`. Do NOT add a separate role check inside the page itself —
 *     route-level + sidebar-level gating already cover it.
 *   • To preview the module without an actual super_admin login, set
 *     `REACT_APP_ENABLE_MARKETING=true` in `.env` and log in as the seeded
 *     super-admin (`demo@enablehealthcare.com`).
 *
 * Implementation note: the env flag is parsed inline (rather than imported
 * from `@/App`) so unit tests can exercise this module without dragging in
 * the full App.js graph (~40 lazy-loaded routes). Matches the pattern used
 * by `marketingFlag.test.jsx`.
 */
import { isSuperAdmin } from "@/lib/permissions";

export const MARKETING_ENABLED =
  String(process.env.REACT_APP_ENABLE_MARKETING || "false").toLowerCase() === "true";

/** Non-hook form — usable from any context (sidebar spec filters, plain JS). */
export function canAccessMarketing(role) {
  if (!MARKETING_ENABLED) return false;
  return isSuperAdmin(role);
}
